import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Brain, Zap, Target, RefreshCw, Check, Flame, Shield, Eye, Dumbbell } from 'lucide-react';
import ShareResult from '../ShareResult';

const ASSESSMENT_QS = [
  { id: 'discomfort', q: 'How often do you voluntarily do things you find uncomfortable?', icon: Flame, color: '#E63946' },
  { id: 'impulse', q: 'When you feel an urge (snacking, scrolling), how often do you resist?', icon: Shield, color: '#00F0FF' },
  { id: 'challenge', q: 'Do you actively seek challenges that push your limits?', icon: Target, color: '#f59e0b' },
  { id: 'persist', q: 'When a task gets boring or hard, do you push through anyway?', icon: Zap, color: '#22c55e' },
  { id: 'routine', q: 'How consistently do you maintain habits you\'ve committed to?', icon: Dumbbell, color: '#818cf8' },
  { id: 'growth', q: 'Do you choose growth over comfort in daily decisions?', icon: Eye, color: '#a78bfa' },
];

const OPTIONS = [
  { label: 'Rarely', score: 1 },
  { label: 'Sometimes', score: 2 },
  { label: 'Often', score: 3 },
  { label: 'Almost Always', score: 4 },
];

const TIERS = [
  { min: 0, max: 10, label: 'Comfort Zone Dweller', desc: 'Your aMCC needs training. Start with one small uncomfortable action daily.', color: '#71717a' },
  { min: 11, max: 16, label: 'Developing', desc: 'You push through sometimes. Build consistency with daily micro-challenges.', color: '#f59e0b' },
  { min: 17, max: 20, label: 'Strong-Willed', desc: 'You regularly choose discomfort over ease. Your aMCC is growing.', color: '#00F0FF' },
  { min: 21, max: 24, label: 'Unbreakable', desc: 'David Goggins territory. Your aMCC is forged in adversity.', color: '#22c55e' },
];

const DAILY_CHALLENGES = [
  { title: 'Cold Exposure', desc: 'End your shower with 30 seconds of the coldest water you can handle.', category: 'Physical', difficulty: 'Medium' },
  { title: 'No Phone First Hour', desc: 'Don\'t touch your phone for the first 60 minutes after waking.', category: 'Mental', difficulty: 'Hard' },
  { title: '100 Push-ups', desc: 'Do 100 push-ups throughout the day, broken into any sets.', category: 'Physical', difficulty: 'Medium' },
  { title: 'Talk to a Stranger', desc: 'Start a genuine conversation with someone you don\'t know.', category: 'Social', difficulty: 'Hard' },
  { title: 'Do It Now', desc: 'The next task you think "I\'ll do it later" — do it immediately instead.', category: 'Mental', difficulty: 'Easy' },
  { title: 'Skip One Meal', desc: 'Fast through lunch or dinner. Notice the discomfort, don\'t cave.', category: 'Physical', difficulty: 'Hard' },
  { title: 'Write 500 Words', desc: 'Journal or write about something difficult — no editing, just flow.', category: 'Mental', difficulty: 'Medium' },
  { title: 'Sit in Silence', desc: 'Sit with zero stimulation (no phone, music, TV) for 15 minutes.', category: 'Mental', difficulty: 'Medium' },
  { title: 'Extra Set', desc: 'At the gym, add one extra set to every exercise when you feel "done."', category: 'Physical', difficulty: 'Medium' },
  { title: 'Apologize First', desc: 'If there\'s an unresolved conflict, reach out and apologize first.', category: 'Social', difficulty: 'Hard' },
];

const DIFFICULTY_COLORS = { Easy: '#22c55e', Medium: '#f59e0b', Hard: '#E63946' };

export default function AnteriorMidcingulateArticle() {
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [challenge, setChallenge] = useState(() => DAILY_CHALLENGES[Math.floor(Math.random() * DAILY_CHALLENGES.length)]);
  const [completedChallenges, setCompletedChallenges] = useState(0);

  const score = Object.values(answers).reduce((s, v) => s + v, 0);
  const tier = TIERS.find(t => score >= t.min && score <= t.max) || TIERS[0];
  const allAnswered = Object.keys(answers).length === ASSESSMENT_QS.length;

  const nextChallenge = () => {
    let next;
    do { next = DAILY_CHALLENGES[Math.floor(Math.random() * DAILY_CHALLENGES.length)]; } while (next.title === challenge.title && DAILY_CHALLENGES.length > 1);
    setChallenge(next);
  };

  const completeChallenge = () => {
    setCompletedChallenges(c => c + 1);
    nextChallenge();
  };

  return (
    <main className="pt-20 min-h-screen bg-[#0a0a0a]" data-testid="amcc-article">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <Link to="/blog" className="text-zinc-500 text-xs uppercase tracking-wider hover:text-white transition-colors mb-4 inline-block" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Back to Blog</Link>

        <h1 className="text-4xl sm:text-5xl text-white mb-4" style={{ fontFamily: 'Bebas Neue, sans-serif' }} data-testid="amcc-title">The Anterior Midcingulate Cortex</h1>
        <p className="text-zinc-400 text-lg mb-8 leading-relaxed">The brain region that grows when you do hard things — and shrinks when you stop. Here's how to train it.</p>

        {/* Article Content */}
        <div className="prose-ninja mb-10 space-y-6 text-zinc-300 leading-relaxed">
          <h2 className="text-white text-2xl" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>What Is the aMCC?</h2>
          <p>The anterior midcingulate cortex (aMCC) is a part of the brain that plays a crucial role in willpower, decision-making, and motivation. Scientists have discovered that it <strong className="text-white">physically grows larger</strong> when you engage in challenging activities you don't enjoy — and shrinks when you stop.</p>
          <p>Think of it like a muscle: stress it with challenges, and it adapts by growing. Stop challenging yourself, and it atrophies.</p>

          <h2 className="text-white text-2xl" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Why Does It Matter?</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              { title: 'Decision Making', desc: 'Helps weigh costs and benefits for better choices', color: '#00F0FF' },
              { title: 'Error Detection', desc: 'Alerts you to mistakes and optimizes performance', color: '#E63946' },
              { title: 'Goal-Directed Behavior', desc: 'Drives determination and perseverance', color: '#22c55e' },
              { title: 'Attention Control', desc: 'Directs focus where it\'s needed most', color: '#f59e0b' },
            ].map(b => (
              <div key={b.title} className="border border-zinc-800 p-3 bg-zinc-900/40">
                <div className="w-2 h-2 rounded-full mb-2" style={{ backgroundColor: b.color }} />
                <h4 className="text-white text-sm" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>{b.title}</h4>
                <p className="text-zinc-500 text-xs">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Interactive: Willpower Self-Assessment */}
        <div className="border border-[#00F0FF]/30 bg-[#00F0FF]/5 p-6 mb-10" data-testid="willpower-assessment">
          <div className="flex items-center gap-2 mb-1">
            <Brain className="w-5 h-5 text-[#00F0FF]" />
            <h3 className="text-white text-xl" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>How Strong Is Your aMCC?</h3>
          </div>
          <p className="text-zinc-500 text-xs mb-5">Rate yourself on 6 willpower dimensions.</p>

          <div className="space-y-3">
            {ASSESSMENT_QS.map(q => (
              <div key={q.id} className="border border-zinc-800 bg-zinc-900/60 p-4" data-testid={`amcc-q-${q.id}`}>
                <div className="flex items-center gap-2 mb-2">
                  <q.icon className="w-4 h-4" style={{ color: q.color }} />
                  <p className="text-zinc-300 text-sm">{q.q}</p>
                </div>
                <div className="flex gap-2 ml-6">
                  {OPTIONS.map(o => (
                    <button key={o.label}
                      onClick={() => setAnswers(prev => ({ ...prev, [q.id]: o.score }))}
                      className={`flex-1 py-2 text-xs border transition-all ${answers[q.id] === o.score ? 'border-[#00F0FF]/60 bg-[#00F0FF]/10 text-white' : 'border-zinc-800 text-zinc-500 hover:text-white hover:border-zinc-700'}`}
                      data-testid={`amcc-opt-${q.id}-${o.score}`}>
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {allAnswered && !showResults && (
            <button onClick={() => setShowResults(true)}
              className="w-full mt-5 py-3 bg-[#00F0FF] text-black text-sm uppercase tracking-wider hover:opacity-90 transition-opacity"
              style={{ fontFamily: 'Bebas Neue, sans-serif' }}
              data-testid="amcc-reveal-btn">
              See My Willpower Score
            </button>
          )}

          {showResults && (
            <div className="mt-5 border border-zinc-800 bg-zinc-900/60 p-6 text-center" data-testid="amcc-result">
              <span className="text-5xl font-bold" style={{ fontFamily: 'Bebas Neue, sans-serif', color: tier.color }}>{score}</span>
              <span className="text-zinc-600 text-2xl" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>/{ASSESSMENT_QS.length * 4}</span>
              <p className="text-sm uppercase tracking-wider mt-2 mb-3" style={{ fontFamily: 'Bebas Neue, sans-serif', color: tier.color }}>{tier.label}</p>
              <p className="text-zinc-400 text-sm max-w-md mx-auto mb-4">{tier.desc}</p>
              <div className="flex items-center justify-center gap-3">
                <ShareResult
                  text={`My aMCC Willpower Score: ${score}/${ASSESSMENT_QS.length * 4} — ${tier.label}! How strong is your willpower?`}
                  url="https://ninjathlete.com/blogs/article/the-anterior-midcingulate-cortex"
                  label="Share Score"
                />
                <button onClick={() => { setAnswers({}); setShowResults(false); }}
                  className="px-4 py-2 border border-zinc-700 text-zinc-400 text-xs uppercase tracking-wider hover:text-white transition-colors"
                  data-testid="amcc-retake">Retake</button>
              </div>
            </div>
          )}
        </div>

        {/* Interactive: Daily Challenge Generator */}
        <div className="border border-[#E63946]/30 bg-[#E63946]/5 p-6 mb-10" data-testid="daily-challenge-generator">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Flame className="w-5 h-5 text-[#E63946]" />
              <h3 className="text-white text-xl" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Daily aMCC Challenge</h3>
            </div>
            {completedChallenges > 0 && (
              <span className="text-[10px] px-2 py-1 border border-[#22c55e]/40 text-[#22c55e]" data-testid="completed-count">{completedChallenges} completed</span>
            )}
          </div>

          <div className="bg-zinc-900/60 border border-zinc-800 p-5 mb-4">
            <div className="flex items-center gap-3 mb-2">
              <span className="text-[10px] uppercase tracking-wider font-bold" style={{ color: DIFFICULTY_COLORS[challenge.difficulty] }}>{challenge.difficulty}</span>
              <span className="text-[10px] text-zinc-600 uppercase tracking-wider">{challenge.category}</span>
            </div>
            <h4 className="text-white text-lg mb-2" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>{challenge.title}</h4>
            <p className="text-zinc-400 text-sm leading-relaxed">{challenge.desc}</p>
          </div>

          <div className="flex gap-3">
            <button onClick={completeChallenge}
              className="flex-1 py-2.5 bg-[#22c55e] text-black text-xs uppercase tracking-wider hover:opacity-90 transition-opacity flex items-center justify-center gap-2"
              style={{ fontFamily: 'Bebas Neue, sans-serif' }}
              data-testid="complete-challenge-btn">
              <Check className="w-4 h-4" /> I Did It
            </button>
            <button onClick={nextChallenge}
              className="px-4 py-2.5 border border-zinc-700 text-zinc-400 text-xs uppercase tracking-wider hover:text-white transition-colors flex items-center gap-2"
              style={{ fontFamily: 'Bebas Neue, sans-serif' }}
              data-testid="skip-challenge-btn">
              <RefreshCw className="w-3.5 h-3.5" /> New Challenge
            </button>
          </div>
        </div>

        {/* Article Continuation */}
        <div className="prose-ninja space-y-6 text-zinc-300 leading-relaxed mb-10">
          <h2 className="text-white text-2xl" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>How to Grow Your aMCC</h2>
          <p>The key is <strong className="text-white">doing things you don't want to do</strong>. If you start enjoying an activity, it stops challenging the aMCC. You must continuously find new challenges.</p>
          <ul className="space-y-2">
            <li><strong className="text-white">Physical challenges</strong> — Going to the gym when you don't want to. Cold plunges. Extra reps.</li>
            <li><strong className="text-white">Mental focus</strong> — Deep reading, meditation, learning new skills.</li>
            <li><strong className="text-white">Error correction</strong> — Analyzing mistakes, accepting constructive criticism.</li>
            <li><strong className="text-white">Goal discipline</strong> — Setting deadlines and sticking to them, especially when you don't feel like it.</li>
          </ul>

          <p>The activity of the midcingulate cortex is linked to success in various tasks, from overcoming challenges to achieving long-term goals. The key is consistency — just like muscles, not using it will make it shrink back.</p>
        </div>

        {/* Cross-links */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Link to="/blogs/article/how-cold-should-a-cold-plunge-be" className="group bg-zinc-900/80 border border-zinc-800 hover:border-[#00F0FF]/40 p-4 transition-all" data-testid="crosslink-cold-plunge">
            <h4 className="text-white text-sm group-hover:text-[#00F0FF] transition-colors" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Cold Plunge Guide</h4>
            <p className="text-zinc-600 text-xs mt-1">Train your aMCC with cold exposure</p>
          </Link>
          <Link to="/tools/biohacking-score" className="group bg-zinc-900/80 border border-zinc-800 hover:border-[#E63946]/40 p-4 transition-all" data-testid="crosslink-biohacking-score">
            <h4 className="text-white text-sm group-hover:text-[#E63946] transition-colors" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Biohacking Score Quiz</h4>
            <p className="text-zinc-600 text-xs mt-1">Rate your full biohacking protocol</p>
          </Link>
        </div>
      </div>
    </main>
  );
}
